import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';
import { api } from '../../utils/apiClient';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useUser();
  
  // Get token from URL
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tokenVerified, setTokenVerified] = useState<boolean | null>(null);
  const [tokenError, setTokenError] = useState('');
  
  // Refs
  const passwordInputRef = useRef<HTMLInputElement>(null);
  
  // Set meta tags
  useMetaTags(
    t('auth.resetPassword', 'Reset Password'),
    t('auth.resetPasswordDescription', 'Create a new password for your account')
  );
  
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/settings');
    }
  }, [isLoggedIn, navigate]);
  
  // Verify token when component loads
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenVerified(false);
        setTokenError(t('auth.missingToken', 'Password reset token is missing'));
        return;
      }
      
      try {
        const response = await api.auth.verifyResetToken(token);
        
        if (response.success) {
          setTokenVerified(true);
          // Focus password input once token is verified
          setTimeout(() => {
            passwordInputRef.current?.focus();
          }, 100);
        } else {
          setTokenVerified(false);
          setTokenError(t('auth.invalidToken', 'Password reset token is invalid or has expired'));
        }
      } catch (error) {
        console.error('Token verification error:', error);
        setTokenVerified(false);
        setTokenError(t('auth.tokenVerificationFailed', 'Failed to verify reset token'));
      }
    };
    
    verifyToken();
  }, [token, t]);
  
  // Validate password
  const validatePassword = (pass: string): boolean => {
    // Minimum length check
    if (pass.length < 8) {
      setPasswordError(t('auth.passwordTooShort', 'Password must be at least 8 characters long'));
      return false;
    }
    
    // Reset error
    setPasswordError('');
    return true;
  };
  
  // Check if passwords match
  const validatePasswordMatch = (pass1: string, pass2: string): boolean => {
    if (pass1 !== pass2) {
      setConfirmPasswordError(t('auth.passwordsDontMatch', 'Passwords do not match'));
      return false;
    }
    
    // Reset error
    setConfirmPasswordError('');
    return true;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setFormError('');
    
    // Validate all fields
    const isPasswordValid = validatePassword(password);
    const doPasswordsMatch = validatePasswordMatch(password, confirmPassword);
    
    if (!isPasswordValid || !doPasswordsMatch) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Call API to reset password
      const response = await api.auth.resetPassword(token as string, password);
      
      if (response.success) {
        setSuccessMessage(t('auth.passwordResetSuccess', 'Your password has been reset successfully. You can now log in with your new password.'));
        setPassword('');
        setConfirmPassword('');
      } else {
        setFormError(response.message || t('auth.passwordResetFailed', 'Password reset failed. Please try again.'));
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setFormError(t('auth.passwordResetFailed', 'Password reset failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render loading state while verifying token
  if (tokenVerified === null) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
            </div>
            <p className="mt-3">{t('auth.verifyingToken', 'Verifying reset token...')}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h1 className="h4 mb-4 text-center">{t('auth.resetPassword', 'Reset Password')}</h1>
              
              {/* Error for invalid token */}
              {!tokenVerified && (
                <div className="alert alert-danger" role="alert">
                  <p>{tokenError}</p>
                  <div className="mt-3">
                    <Link to="/forgot-password" className="btn btn-primary">
                      {t('auth.requestNewToken', 'Request New Reset Link')}
                    </Link>
                  </div>
                </div>
              )}
              
              {/* Success message after reset */}
              {successMessage && (
                <div className="alert alert-success" role="alert">
                  <p>{successMessage}</p>
                  <div className="mt-3">
                    <Link to="/login" className="btn btn-primary">
                      {t('auth.goToLogin', 'Go to Login')}
                    </Link>
                  </div>
                </div>
              )}
              
              {/* Form for resetting password */}
              {tokenVerified && !successMessage && (
                <>
                  <p className="text-muted mb-4">
                    {t('auth.resetPasswordInstructions', 'Please enter your new password below.')}
                  </p>
                  
                  {formError && (
                    <div className="alert alert-danger" role="alert">
                      {formError}
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    {/* New Password */}
                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">
                        {t('auth.newPassword', 'New Password')}
                      </label>
                      <input
                        ref={passwordInputRef}
                        type="password"
                        className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('auth.passwordPlaceholder', 'Min. 8 characters')}
                        required
                        minLength={8}
                      />
                      {passwordError && (
                        <div className="invalid-feedback">{passwordError}</div>
                      )}
                      {password && <PasswordStrengthMeter password={password} />}
                    </div>
                    
                    {/* Confirm New Password */}
                    <div className="mb-4">
                      <label htmlFor="confirmPassword" className="form-label">
                        {t('auth.confirmPassword', 'Confirm Password')}
                      </label>
                      <input
                        type="password"
                        className={`form-control ${confirmPasswordError ? 'is-invalid' : ''}`}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('auth.confirmPasswordPlaceholder', 'Re-enter your password')}
                        required
                        minLength={8}
                      />
                      {confirmPasswordError && (
                        <div className="invalid-feedback">{confirmPasswordError}</div>
                      )}
                    </div>
                    
                    {/* Submit Button */}
                    <div className="d-grid">
                      <button 
                        type="submit" 
                        className="btn btn-primary" 
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('common.loading', 'Loading...')}
                          </>
                        ) : (
                          t('auth.resetPassword', 'Reset Password')
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
              
              {/* Back to Login */}
              {!successMessage && (
                <div className="mt-4 text-center">
                  <Link to="/login" className="text-primary">
                    {t('auth.backToLogin', 'Back to Login')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword; 